import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { usePreviewPng, uploadShareCard, isDespiaIOS, saveToIOSPhotos } from '@/utils/shareCard';
import { toBlob } from 'html-to-image';
import { X, Share2, Link2, Check, Users, MessageSquare, Download, Loader2, ListChecks, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { API_URL as API, BACKEND_URL } from '@/utils/apiConfig';
import { shareViaNative } from '@/utils/nativeShare';
import { useFeatureFlag } from '@/contexts/FeatureFlagsContext';

import { useTranslation } from 'react-i18next';

/**
 * ShareVerseModal — share a Bible verse (single or contiguous range)
 * internally or externally.
 *
 *   Internal channels:
 *     • Send to a friend (Direct Share — chat-style bubble in their
 *       community shared-thread). Existing /api/community/share endpoint.
 *     • Post to community feed. Existing /api/feed/posts endpoint.
 *
 *   External:
 *     • Copy link / Native share-sheet — opens the SharedVersePage
 *       (`/share/verse/<share_id>`).
 *     • Share image — same link plus a themed SVG card the user picks.
 *
 *   Image generation:
 *     • Captured client-side from the live preview (utils/shareCard)
 *       (1200×630 PNG via PIL — the universal format for OG unfurls).
 *     • The share URL itself is `/api/bible/share/<share_id>/og?theme=X`:
 *       bots get OG-tagged HTML so WhatsApp/iMessage/Twitter unfurl
 *       with the verse card; humans 302 to the React SPA at
 *       `/share/verse/<share_id>`.
 */

const THEMES = [
  // ── Verse-specific themes (Bible reader exclusive) ──
  {
    key: 'light',
    label: 'Light',
    bg: '#FFFFFF',
    bg2: '#F4F4F6',
    text: '#0F172A',
    accent: '#1E293B',
    muted: '#475569',
    border: '#E2E8F0',
  },
  {
    key: 'dark',
    label: 'Dark',
    bg: '#0B1220',
    bg2: '#111827',
    text: '#F3F4F6',
    accent: '#A78BFA',
    muted: '#94A3B8',
    border: '#1F2937',
  },
  {
    key: 'minimal',
    label: 'Parchment',
    bg: '#F5F0E8',
    bg2: '#EDE6D8',
    text: '#2C2416',
    accent: '#8B6F47',
    muted: '#6B5E4E',
    border: '#D4C9B8',
  },
  {
    key: 'scenic',
    label: 'Scenic',
    bg: '#0F2A44',
    bg2: '#1B3A5A',
    text: '#F8FAFC',
    accent: '#F4B860',
    muted: '#A8C0D6',
    border: '#1F3A56',
  },
  // ── Unified palette (parity with chat / reflection / DYK / recap) ──
  // Mapped from SHARE_THEMES (frontend/src/components/shareThemes.js)
  // with `secondary→muted` and `bg2` derived from the swatch outer tone.
  {
    key: 'classic',
    label: 'Classic',
    bg: '#FFFFFF',
    bg2: '#F1F5F9',
    text: '#1a1a2e',
    accent: '#6366f1',
    muted: '#64748b',
    border: '#e2e8f0',
  },
  {
    key: 'midnight',
    label: 'Midnight',
    bg: '#0f172a',
    bg2: '#1e293b',
    text: '#f1f5f9',
    accent: '#818cf8',
    muted: '#94a3b8',
    border: '#1e293b',
  },
  {
    key: 'scripture',
    label: 'Scripture',
    bg: '#fef3c7',
    bg2: '#fde68a',
    text: '#451a03',
    accent: '#92400e',
    muted: '#78350f',
    border: '#fbbf24',
  },
  {
    key: 'ocean',
    label: 'Ocean',
    bg: '#0c4a6e',
    bg2: '#075985',
    text: '#f0f9ff',
    accent: '#38bdf8',
    muted: '#bae6fd',
    border: '#075985',
  },
  {
    key: 'mono',
    label: 'Mono',
    bg: '#fafafa',
    bg2: '#f4f4f5',
    text: '#18181b',
    accent: '#18181b',
    muted: '#71717a',
    border: '#d4d4d8',
  },
];

/* ── Local visual preview of the SVG share card (cheap React render). ── */
const CARD_CHAR_BUDGET = 750;
const MAX_CARDS = 10;

// Split verses into cards without ever cutting a verse. `count` null = auto
// (fit ~750 chars per portrait card); a number = balance into that many.
export function paginateVerses(verses, count) {
  if (!verses.length) return [verses];
  const total = verses.reduce((n, v) => n + v.text.length, 0);
  const budget = count ? Math.ceil(total / count) : CARD_CHAR_BUDGET;
  const pages = [];
  let cur = []; let len = 0;
  verses.forEach((v) => {
    const remaining = count ? count - pages.length - 1 : Infinity;
    if (cur.length && len + v.text.length > budget && remaining > 0) {
      pages.push(cur); cur = []; len = 0;
    }
    cur.push(v); len += v.text.length;
  });
  if (cur.length) pages.push(cur);
  return pages;
}

// "Genesis 1" / "John 3:16-18" + page verses -> "Genesis 1:1–8"
function pageReference(reference, pageVerses) {
  const base = reference.replace(/:.*$/, '');
  const first = pageVerses[0]?.verse;
  const last = pageVerses[pageVerses.length - 1]?.verse;
  if (first == null) return reference;
  return first === last ? `${base}:${first}` : `${base}:${first}\u2013${last}`;
}

function VerseCardPreview({ theme, reference, translation, verses, pageLabel }) {
  const {
    t
  } = useTranslation();

  const multi = verses.length > 1;
  return (
    <div
      style={{
        background: `linear-gradient(180deg, ${theme.bg} 0%, ${theme.bg2} 100%)`,
        padding: '28px 22px',
        position: 'relative',
        overflow: 'hidden',
        fontFamily: 'Georgia, "Times New Roman", serif',
        flex: 1,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Decorative giant quote mark */}
      <div
        style={{
          position: 'absolute',
          top: 30,
          left: '50%',
          transform: 'translateX(-50%)',
          fontSize: 220,
          color: theme.accent,
          opacity: 0.07,
          fontWeight: 700,
          lineHeight: 1,
          pointerEvents: 'none',
        }}
      >
        “
      </div>
      {/* Brand */}
      <div
        style={{
          position: 'relative',
          textAlign: 'center',
          fontSize: 9,
          letterSpacing: '0.32em',
          color: theme.accent,
          fontWeight: 700,
          marginBottom: 4,
          fontFamily: '-apple-system, "Helvetica Neue", sans-serif',
        }}
      >
        {t('generated.shareversemodal_eili_scripture_b5c1')}
      </div>
      <div
        style={{
          width: 40,
          height: 1.5,
          background: theme.accent,
          opacity: 0.5,
          margin: '0 auto 22px',
        }}
      />
      {/* Verse body */}
      <div
        style={{
          fontSize: verses.length > 3 ? 13 : 16,
          lineHeight: 1.55,
          color: theme.text,
          textAlign: 'center',
          marginTop: 'auto',
          marginBottom: 'auto',
          paddingBottom: 24,
          position: 'relative',
        }}
      >
        {verses.map((v, i) => (
          <span key={i}>
            {multi && (
              <sup style={{ color: theme.accent, fontWeight: 700, marginRight: 4 }}>
                {v.verse}
              </sup>
            )}
            {v.text}{' '}
          </span>
        ))}
      </div>
      {/* Reference + translation */}
      <div
        style={{
          textAlign: 'center',
          fontSize: 12,
          letterSpacing: '0.18em',
          color: theme.accent,
          fontWeight: 700,
          fontFamily: '-apple-system, "Helvetica Neue", sans-serif',
        }}
      >
        {reference.toUpperCase()}
      </div>
      <div
        style={{
          textAlign: 'center',
          fontSize: 9,
          letterSpacing: '0.28em',
          color: theme.muted,
          fontWeight: 500,
          marginTop: 4,
          marginBottom: 18,
          fontFamily: '-apple-system, "Helvetica Neue", sans-serif',
        }}
      >
        {translation.toUpperCase()}{pageLabel ? ` \u00B7 ${pageLabel}` : ''}
      </div>
      {/* Footer */}
      <div style={{ borderTop: `1px solid ${theme.border}`, paddingTop: 12, textAlign: 'center' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
          <img
            src="/logo.png"
            alt={t('generated.shareversemodal_eili_4412')}
            style={{ width: 28, height: 28, objectFit: 'contain' }}
            crossOrigin="anonymous"
          />
          <span
            style={{
              fontFamily: "'Playwrite ES Deco', cursive",
              fontSize: 14,
              color: theme.accent,
              fontWeight: 700,
              marginTop: 3,
            }}
          >
            {t('generated.shareversemodal_eili_4412')}
          </span>
        </div>
        <div
          style={{
            fontSize: 9,
            letterSpacing: '0.2em',
            color: theme.muted,
            marginTop: 18,
            fontFamily: '-apple-system, "Helvetica Neue", sans-serif',
          }}
        >
          askeili.com
        </div>
      </div>
    </div>
  );
}

function CardCountPicker({ value, autoCount, max, onChange }) {
  const opts = [null, ...Array.from({ length: max }, (_, i) => i + 1)];
  return (
    <div className="flex items-center gap-1.5 mb-2.5 overflow-x-auto" style={{ scrollbarWidth: 'none' }} data-testid="share-verse-card-count">
      <span className="text-[10px] font-semibold uppercase tracking-wider mr-1 shrink-0" style={{ color: 'var(--theme-textSecondary)' }}>Cards</span>
      {opts.map((o) => {
        const active = value === o;
        return (
          <button
            key={o ?? 'auto'}
            type="button"
            onClick={() => onChange(o)}
            className="shrink-0 px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors"
            style={{
              background: active ? 'var(--theme-accent)' : 'var(--glass-bg)',
              color: active ? 'var(--theme-bg, #fff)' : 'var(--theme-text)',
              border: '1px solid var(--glass-border)',
            }}
            data-testid={`share-verse-card-count-${o ?? 'auto'}`}
          >
            {o == null ? `Auto (${autoCount})` : o}
          </button>
        );
      })}
    </div>
  );
}

// Swipeable, scroll-snapped card preview with dots + arrows.
function CardCarousel({ count, index, onIndex, children }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const onScroll = () => {
      const i = Math.round(el.scrollLeft / (el.clientWidth || 1));
      if (i !== index) onIndex(i);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [index, onIndex]);
  useEffect(() => {
    if (index === 0 && ref.current) ref.current.scrollTo({ left: 0 });
  }, [count]); // eslint-disable-line react-hooks/exhaustive-deps
  const go = (i) => {
    const el = ref.current;
    if (el) el.scrollTo({ left: i * el.clientWidth, behavior: 'smooth' });
    onIndex(i);
  };
  return (
    <div className="relative" data-testid="share-verse-preview">
      <div
        ref={ref}
        className="flex items-stretch overflow-x-auto snap-x snap-mandatory rounded-xl"
        style={{ border: '1px solid var(--glass-border)', scrollbarWidth: 'none' }}
      >
        {React.Children.map(children, (child) => (
          <div className="w-full shrink-0 snap-center flex flex-col">{child}</div>
        ))}
      </div>
      {count > 1 && (
        <>
          <button type="button" onClick={() => go(Math.max(0, index - 1))} disabled={index === 0}
            className="absolute left-1.5 top-[45%] h-8 w-8 rounded-full flex items-center justify-center bg-black/40 text-white disabled:opacity-0 transition-opacity"
            aria-label="Previous card" data-testid="share-verse-prev-card">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button type="button" onClick={() => go(Math.min(count - 1, index + 1))} disabled={index === count - 1}
            className="absolute right-1.5 top-[45%] h-8 w-8 rounded-full flex items-center justify-center bg-black/40 text-white disabled:opacity-0 transition-opacity"
            aria-label="Next card" data-testid="share-verse-next-card">
            <ChevronRight className="h-4 w-4" />
          </button>
          <div className="flex justify-center gap-1.5 mt-2.5" data-testid="share-verse-card-dots">
            {Array.from({ length: count }, (_, i) => (
              <button key={i} type="button" onClick={() => go(i)} aria-label={`Card ${i + 1}`}
                data-testid={`share-verse-card-dot-${i}`}
                className="h-1.5 rounded-full transition-[width,opacity]"
                style={{ width: i === index ? 16 : 6, background: 'var(--theme-accent)', opacity: i === index ? 1 : 0.35 }} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function ShareVerseModal({
  reference,
  verses,
  translation,
  onClose,
  onAddMoreVerses,
}) {
  const {
    t
  } = useTranslation();

  const [selectedThemeKey, setSelectedThemeKey] = useState('light');
  const [shareId, setShareId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [postingFeed, setPostingFeed] = useState(false);
  const [postedFeed, setPostedFeed] = useState(false);
  const [showFriendPicker, setShowFriendPicker] = useState(false);
  const [friends, setFriends] = useState(null);
  const [friendsLoading, setFriendsLoading] = useState(false);
  const [sentToFriend, setSentToFriend] = useState(null);
  const createPromiseRef = useRef(null);
  // Gate the "Friend" + "Feed" buttons (share-to-community actions).
  const { enabled: communityShareEnabled } = useFeatureFlag('community_share');

  const theme = THEMES.find((t) => t.key === selectedThemeKey) || THEMES[0];
  // The official image is a capture of the live preview, so they always match.
  const [cardCount, setCardCount] = useState(null); // null = auto
  const pages = useMemo(() => paginateVerses(verses, cardCount), [verses, cardCount]);
  const autoPageCount = useMemo(() => paginateVerses(verses, null).length, [verses]);
  const [pageIdx, setPageIdx] = useState(0);
  const pageRefs = useRef([]);
  const cardRef = useRef(null); // first card — used for link previews
  const pngBlob = usePreviewPng(cardRef, `${selectedThemeKey}|${reference}|${verses.length}|${pages.length}`, 1080);
  const pageMeta = (i) => (pages.length > 1
    ? { ref: pageReference(reference, pages[i]), label: `${i + 1} of ${pages.length}` }
    : { ref: reference, label: null });
  const fileBase = `eili-${reference.replace(/[: ]+/g, '-')}-${selectedThemeKey}`;
  const pageFileName = (i) => (pages.length > 1 ? `${fileBase}-${i + 1}of${pages.length}.png` : `${fileBase}.png`);
  const renderPage = async (i) => {
    const el = pageRefs.current[i];
    return el ? toBlob(el, { pixelRatio: 2, skipAutoScale: true, cacheBust: false }) : null;
  };
  const syncCard = (id) => uploadShareCard(id, pngBlob);

  // Lazily mint the share_id on first action that needs it.
  const ensureShareId = useCallback(async () => {
    if (shareId) return shareId;
    if (createPromiseRef.current) return createPromiseRef.current;
    setCreating(true);
    const p = (async () => {
      try {
        const res = await axios.post(
          `${API}/bible/share`,
          { reference, verses, translation, theme: selectedThemeKey },
          { withCredentials: true }
        );
        setShareId(res.data.share_id);
        return res.data.share_id;
      } catch (err) {
        // Surface the real reason so the user knows what to do — the
        // backend already returns clear 400 messages (verse cap, missing
        // reference, etc.). Falling back to the generic toast otherwise.
        const detail = err?.response?.data?.detail;
        if (detail) {
          toast.error(detail);
        } else {
          toast.error(t('generated.shareversemodal_could_not_prepare_207e'));
        }
        return null;
      } finally {
        setCreating(false);
        createPromiseRef.current = null;
      }
    })();
    createPromiseRef.current = p;
    return p;
  }, [shareId, reference, verses, translation, selectedThemeKey]);

  // Eagerly create the share so the buttons are instant.
  useEffect(() => {
    ensureShareId();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Public share URL: points at the API's OG-tagged route. Bots
  // (WhatsApp/iMessage/Twitter/Slack) get an HTML page with og:image →
  // PNG card → link unfurls beautifully. Humans get a 302 to the React
  // SPA at /share/verse/<id> which renders the polished landing page.
  const shareUrl = shareId
    ? `${BACKEND_URL}/api/bible/share/${shareId}/og?theme=${selectedThemeKey}`
    : null;
  const imageUrl = shareId
    ? `${BACKEND_URL}/api/bible/share/${shareId}/image.png?theme=${selectedThemeKey}`
    : null;

  const verseTextPlain = verses
    .map((v) => (verses.length > 1 ? `${v.verse} ${v.text}` : v.text))
    .join(' ');

  // Helper: build the public (OG-tagged) share URL given a share_id.
  // Kept here so all callers share one definition and we never mix
  // SPA paths with API OG paths.
  const buildShareUrl = (id) =>
    `${BACKEND_URL}/api/bible/share/${id}/og?theme=${selectedThemeKey}`;
  const buildImageUrl = (id) =>
    `${BACKEND_URL}/api/bible/share/${id}/image.png?theme=${selectedThemeKey}`;

  const handleCopyLink = async () => {
    const id = await ensureShareId();
    if (!id) return;
    syncCard(id);
    const url = buildShareUrl(id);
    const text = `"${verseTextPlain}"\n\n— ${reference} (${translation.toUpperCase()})\n\n${url}`;
    try {
      await navigator.clipboard.writeText(text);
      setLinkCopied(true);
      toast.success(t('generated.shareversemodal_copied_74e0'));
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      toast.error(t('generated.shareversemodal_copy_failed_6ac1'));
    }
  };

  const handleNativeShare = async () => {
    const id = await ensureShareId();
    if (!id) return;
    syncCard(id);
    const url = buildShareUrl(id);
    const text = `"${verseTextPlain}"\n\n— ${reference} (${translation.toUpperCase()})`;
    const result = await shareViaNative({
      title: `${reference} — Eili`,
      text,
      url,
    });
    if (!result.shared && !result.aborted) {
      try {
        await navigator.clipboard.writeText(`${text}\n\n${url}`);
        toast.success(t('generated.shareversemodal_copied_paste_anywhere_8bf9'));
      } catch {
        toast.error('Could not copy. Try the Copy button instead.');
      }
    }
  };

  const handleShareImage = async () => {
    const id = await ensureShareId();
    if (!id) return;
    const url = buildShareUrl(id);
    const text = `"${verseTextPlain.length > 180 ? verseTextPlain.slice(0, 177) + '…' : verseTextPlain}" — ${reference}`;
    try {
      // PNG is the universal format every share target accepts (iOS
      // Photos, WhatsApp, iMessage, Telegram, X, Facebook, Snapchat,
      // Instagram, Discord, etc.). Falls back to a plain URL+text share
      // if file-attach isn't supported by the underlying share sheet.
      if (!pngBlob) return;
      syncCard(id);
      const file = new File([pngBlob], pageFileName(0), { type: 'image/png' });
      let files;
      if (pages.length > 1) {
        const blobs = await Promise.all(pages.map((_, i) => renderPage(i)));
        files = blobs.filter(Boolean).map((b, i) => new File([b], pageFileName(i), { type: 'image/png' }));
      }
      const result = await shareViaNative({
        title: `${reference} — Eili`,
        text,
        url,
        file: files?.[pageIdx] || file,
        files,
      });
      if (!result.shared && !result.aborted) {
        await navigator.clipboard.writeText(`${text}\n\n${url}`);
        toast.success(t('generated.shareversemodal_copied_image_link_1494'));
      }
    } catch {
      toast.error(t('generated.shareversemodal_share_failed_06ac'));
    }
  };

  // Lazy-fetch friends the first time the picker opens.
  useEffect(() => {
    if (!showFriendPicker || friends !== null || friendsLoading) return;
    let cancelled = false;
    (async () => {
      setFriendsLoading(true);
      try {
        const res = await axios.get(`${API}/community/friends`, { withCredentials: true });
        if (!cancelled) setFriends(res.data?.friends || []);
      } catch {
        if (!cancelled) setFriends([]);
      } finally {
        if (!cancelled) setFriendsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showFriendPicker, friends, friendsLoading]);

  const sendToFriend = async (friendUserId) => {
    const id = await ensureShareId();
    if (!id) return;
    setSentToFriend('sending');
    try {
      const url = buildShareUrl(id);
      const uploaded = await syncCard(id);
      const imgUrl = uploaded ? `${BACKEND_URL}/api/share/${id}/og-image?v=${Date.now()}` : buildImageUrl(id);
      const content = `"${verseTextPlain}"\n\n— ${reference} (${translation.toUpperCase()})\n\n${url}`;
      await axios.post(
        `${API}/community/share`,
        {
          friend_user_id: friendUserId,
          content,
          question: reference,
          conversation_title: reference,
          share_image_url: imgUrl,
          share_id: id,
        },
        { withCredentials: true }
      );
      setSentToFriend(friendUserId);
      toast.success(t('generated.shareversemodal_sent_092c'));
      setTimeout(() => {
        setShowFriendPicker(false);
        setSentToFriend(null);
      }, 800);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not send');
      setSentToFriend(null);
    }
  };

  // Mirrors ShareReflectionModal.handleDownload exactly (proven on Android).
  const savingRef = useRef(false);
  const [saving, setSaving] = useState(false);
  const saveOne = async (blob, name) => {
    const isDespiaAndroid = document.body?.classList?.contains('despia-android');
    if (isDespiaAndroid) {
      toast.info('Saving to your device…');
      const form = new FormData();
      form.append('file', new File([blob], name, { type: 'image/png' }), 'eili-verse.png');
      const res = await fetch(`${BACKEND_URL}/api/share/upload-image`, { method: 'POST', body: form, credentials: 'include' });
      const data = await res.json();
      if (data?.url) {
        const { default: despia } = await import('despia-native');
        despia(data.url);
      }
    } else if (isDespiaIOS()) {
      await saveToIOSPhotos(blob, name);
    } else {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = name;
      link.href = url;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      if (pages.length === 1) toast.success('Image saved');
    }
  };

  // Saves every card in order (one per verse group).
  const handleDownloadImage = async () => {
    if (savingRef.current || !pageRefs.current[0]) return;
    savingRef.current = true;
    setSaving(true);
    try {
      for (let i = 0; i < pages.length; i += 1) {
        const blob = await renderPage(i);
        if (blob) await saveOne(blob, pageFileName(i));
        // Give the native downloader / browser a beat between files.
        if (i < pages.length - 1) await new Promise((r) => setTimeout(r, 1200));
      }
      if (pages.length > 1) toast.success(`Saved ${pages.length} images`);
    } catch {
      toast.error('Could not save the image. Please try again.');
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const handlePostToFeed = async () => {
    if (postingFeed || postedFeed) return;
    setPostingFeed(true);
    try {
      const id = await ensureShareId();
      if (!id) {
        setPostingFeed(false);
        return;
      }
      syncCard(id);
      const url = buildShareUrl(id);
      // Build a feed-style post body that quotes the verse + link.
      const content = `"${verseTextPlain}"\n\n— ${reference} (${translation.toUpperCase()})\n\n${url}`;
      await axios.post(
        `${API}/feed/posts`,
        { content },
        { withCredentials: true }
      );
      setPostedFeed(true);
      toast.success(t('generated.shareversemodal_posted_to_community_b905'));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not post');
    } finally {
      setPostingFeed(false);
    }
  };

  const handleShareToFriend = () => {
    // Open the inline friend picker; the user then picks a friend and
    // we POST to /api/community/share with the prepared image-share
    // payload (mirroring the existing reflection share pattern).
    setShowFriendPicker(true);
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="share-verse-modal"
    >
      <div
        className="relative w-full sm:max-w-lg flex flex-col rounded-t-2xl sm:rounded-2xl overflow-hidden"
        style={{
          background: 'var(--theme-surface, #1e293b)',
          border: '1px solid var(--glass-border, #334155)',
          maxHeight: 'min(92vh, calc(var(--visual-vh, 100vh) - 16px))',
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-4 border-b"
          style={{ borderColor: 'var(--glass-border, #334155)' }}
        >
          <div className="flex items-center gap-2">
            <Share2 className="h-5 w-5" style={{ color: 'var(--theme-accent)' }} />
            <h3 className="text-base font-semibold" style={{ color: 'var(--theme-text)' }}>
              {t('generated.shareversemodal_share_verse_0f04')}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-white/10"
            data-testid="share-verse-close"
          >
            <X className="h-4 w-4" style={{ color: 'var(--theme-textSecondary)' }} />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Reference badge + "Add more verses" CTA — only shown when
              the modal was opened with a single-verse seed, giving the
              user an obvious path to extend the selection to a range. */}
          <div className="flex items-center justify-between gap-3">
            <div
              className="text-sm font-medium truncate"
              style={{ color: 'var(--theme-text)' }}
              data-testid="share-verse-current-reference"
            >
              {reference}
              <span
                className="ml-2 text-[11px]"
                style={{ color: 'var(--theme-textSecondary)' }}
              >
                {verses.length} verse{verses.length !== 1 ? 's' : ''}
              </span>
            </div>
            {onAddMoreVerses && (
              <button
                onClick={onAddMoreVerses}
                className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full transition-colors whitespace-nowrap shrink-0"
                style={{
                  background: 'var(--theme-accent)',
                  color: 'var(--theme-bg, #fff)',
                  opacity: 0.9,
                }}
                data-testid="share-verse-add-more-btn"
              >
                <ListChecks className="h-3.5 w-3.5" />
                {t('generated.shareversemodal_add_more_verses_f7db')}
              </button>
            )}
          </div>

          {/* Theme picker */}
          <div>
            <p
              className="text-[10px] font-semibold uppercase tracking-wider mb-2.5"
              style={{ color: 'var(--theme-textSecondary)' }}
            >
              {t('generated.shareversemodal_style_ad7b')}
            </p>
            <div className="flex gap-2 flex-wrap" data-testid="share-verse-theme-picker">
              {THEMES.map((t) => {
                const active = selectedThemeKey === t.key;
                return (
                  <button
                    key={t.key}
                    onClick={() => setSelectedThemeKey(t.key)}
                    className="flex flex-col items-center gap-1.5 transition-all"
                    data-testid={`share-verse-theme-${t.key}`}
                  >
                    <div
                      className="w-10 h-10 rounded-full relative overflow-hidden"
                      style={{
                        background: t.bg,
                        border: active ? `2.5px solid ${t.accent}` : '2px solid transparent',
                        boxShadow: active ? `0 0 0 2px ${t.accent}40` : 'none',
                      }}
                    >
                      <div
                        className="absolute bottom-0 right-0 w-5 h-5 rounded-tl-full"
                        style={{ background: t.accent }}
                      />
                    </div>
                    <span
                      className="text-[10px] font-medium"
                      style={{
                        color: active ? 'var(--theme-text)' : 'var(--theme-textSecondary)',
                      }}
                    >
                      {t.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Preview */}
          <div>
            <p
              className="text-[10px] font-semibold uppercase tracking-wider mb-2.5"
              style={{ color: 'var(--theme-textSecondary)' }}
            >
              {t('generated.shareversemodal_preview_e2f5')}
            </p>
            {verses.length > 1 && (
              <CardCountPicker
                value={cardCount}
                autoCount={autoPageCount}
                max={Math.min(verses.length, MAX_CARDS)}
                onChange={(v) => { setCardCount(v); setPageIdx(0); }}
              />
            )}
            <CardCarousel count={pages.length} index={pageIdx} onIndex={setPageIdx}>
              {pages.map((pv, i) => (
                <div
                  key={i}
                  ref={(el) => { pageRefs.current[i] = el; if (i === 0) cardRef.current = el; }}
                  className="flex-1 flex flex-col"
                  data-testid={`share-verse-card-${i}`}
                >
                  <VerseCardPreview
                    theme={theme}
                    reference={pageMeta(i).ref}
                    translation={translation}
                    verses={pv}
                    pageLabel={pageMeta(i).label}
                  />
                </div>
              ))}
            </CardCarousel>
            {imageUrl && (
              <p
                className="text-[10px] mt-2 text-center"
                style={{ color: 'var(--theme-textSecondary)' }}
              >
                {t('generated.shareversemodal_the_shared_image_201a')}
              </p>
            )}
          </div>
        </div>

        {/* Actions */}
        <div
          className="px-5 py-4 border-t space-y-2.5"
          style={{
            borderColor: 'var(--glass-border, #334155)',
            paddingBottom: 'max(1rem, var(--safe-area-bottom, env(safe-area-inset-bottom, 0px)))',
          }}
        >
          {/* Primary row — external share */}
          <div className="flex gap-2.5">
            <Button
              onClick={handleShareImage}
              disabled={creating || !pngBlob}
              className="flex-1 rounded-xl py-3 font-medium gap-2"
              style={{ background: theme.accent, color: theme.key === 'light' ? '#FFFFFF' : theme.bg }}
              data-testid="share-verse-image-btn"
            >
              {creating || !pngBlob ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Share2 className="h-4 w-4" />
              )}
              {creating || !pngBlob ? 'Preparing…' : 'Share Image'}
            </Button>
            <Button
              onClick={handleNativeShare}
              disabled={creating || !pngBlob}
              variant="outline"
              className="rounded-xl py-3 font-medium gap-2"
              style={{ borderColor: 'var(--glass-border)', color: 'var(--theme-text)' }}
              data-testid="share-verse-link-share-btn"
            >
              <Share2 className="h-4 w-4" />
              {t('generated.shareversemodal_text_b4c6')}
            </Button>
          </div>

          {/* Secondary row — copy / save / internal */}
          <div className="grid grid-cols-4 gap-2">
            <Button
              onClick={handleCopyLink}
              disabled={!pngBlob}
              variant="outline"
              className="rounded-xl py-3 font-medium gap-1.5 flex-col h-auto"
              style={{ borderColor: 'var(--glass-border)', color: 'var(--theme-text)' }}
              data-testid="share-verse-copy-btn"
            >
              {linkCopied ? <Check className="h-4 w-4" /> : <Link2 className="h-4 w-4" />}
              <span className="text-[11px]">{linkCopied ? 'Copied' : 'Copy'}</span>
            </Button>
            <Button
              onClick={handleDownloadImage}
              disabled={!pngBlob || saving}
              variant="outline"
              className="rounded-xl py-3 font-medium gap-1.5 flex-col h-auto"
              style={{ borderColor: 'var(--glass-border)', color: 'var(--theme-text)' }}
              data-testid="share-verse-save-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              <span className="text-[11px]">{pages.length > 1 ? `Save all ${pages.length}` : t('generated.shareversemodal_save_26df')}</span>
            </Button>
            {communityShareEnabled && (
            <Button
              onClick={handleShareToFriend}
              variant="outline"
              className="rounded-xl py-3 font-medium gap-1.5 flex-col h-auto"
              style={{ borderColor: 'var(--glass-border)', color: 'var(--theme-text)' }}
              data-testid="share-verse-friend-btn"
            >
              <Users className="h-4 w-4" />
              <span className="text-[11px]">{t('generated.shareversemodal_friend_bcb7')}</span>
            </Button>
            )}
            {communityShareEnabled && (
            <Button
              onClick={handlePostToFeed}
              disabled={postingFeed || postedFeed}
              variant="outline"
              className="rounded-xl py-3 font-medium gap-1.5 flex-col h-auto"
              style={{ borderColor: 'var(--glass-border)', color: 'var(--theme-text)' }}
              data-testid="share-verse-feed-btn"
            >
              {postedFeed ? (
                <Check className="h-4 w-4" />
              ) : postingFeed ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <MessageSquare className="h-4 w-4" />
              )}
              <span className="text-[11px]">{postedFeed ? 'Posted' : 'Feed'}</span>
            </Button>
            )}
          </div>
        </div>

        {/* ── Friend picker overlay ────────────────────────────────────
            Slides in over the main modal content when "Friend" is
            tapped. Lists accepted friends; one tap sends. Designed as
            an overlay rather than a new modal so the user keeps their
            theme + verse context. */}
        {showFriendPicker && (
          <div
            className="absolute inset-0 z-10 flex flex-col"
            style={{ background: 'var(--theme-surface, #1e293b)' }}
            data-testid="share-verse-friend-picker"
          >
            <div
              className="flex items-center gap-3 px-5 py-4 border-b"
              style={{ borderColor: 'var(--glass-border, #334155)' }}
            >
              <button
                onClick={() => setShowFriendPicker(false)}
                className="text-sm font-medium"
                style={{ color: 'var(--theme-accent)' }}
                data-testid="share-verse-friend-back"
              >
                {t('generated.shareversemodal_back_eed1')}
              </button>
              <h3
                className="text-base font-semibold flex-1 text-center pr-12"
                style={{ color: 'var(--theme-text)' }}
              >
                {t('generated.shareversemodal_send_to_a_c5ad')}
              </h3>
            </div>
            <div className="flex-1 overflow-y-auto px-3 py-3">
              {friendsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-5 w-5 animate-spin" style={{ color: 'var(--theme-textSecondary)' }} />
                </div>
              ) : friends && friends.length > 0 ? (
                <ul className="space-y-1">
                  {friends.map((f) => {
                    const sending = sentToFriend === 'sending';
                    const sent = sentToFriend === f.user_id;
                    return (
                      <li key={f.user_id}>
                        <button
                          onClick={() => !sending && !sent && sendToFriend(f.user_id)}
                          disabled={sending || sent}
                          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/5 transition-colors text-left disabled:opacity-50"
                          data-testid={`share-verse-friend-${f.user_id}`}
                        >
                          {f.picture ? (
                            <img src={f.picture} alt="" className="w-9 h-9 rounded-full object-cover" />
                          ) : (
                            <div
                              className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold"
                              style={{
                                background: 'var(--theme-accent)',
                                color: 'var(--theme-bg, #0f172a)',
                              }}
                            >
                              {(f.name || f.username || '?').slice(0, 1).toUpperCase()}
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p
                              className="text-sm font-medium truncate"
                              style={{ color: 'var(--theme-text)' }}
                            >
                              {f.name || f.username || 'Friend'}
                            </p>
                            {f.username && f.name && (
                              <p
                                className="text-xs truncate"
                                style={{ color: 'var(--theme-textSecondary)' }}
                              >
                                @{f.username}
                              </p>
                            )}
                          </div>
                          {sent && <Check className="h-5 w-5" style={{ color: 'var(--theme-accent)' }} />}
                          {sending && <Loader2 className="h-4 w-4 animate-spin" style={{ color: 'var(--theme-textSecondary)' }} />}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <div className="text-center py-12 px-6">
                  <Users
                    className="h-10 w-10 mx-auto mb-3 opacity-40"
                    style={{ color: 'var(--theme-textSecondary)' }}
                  />
                  <p className="text-sm" style={{ color: 'var(--theme-textSecondary)' }}>
                    {t('generated.shareversemodal_add_friends_in_583c')}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
